import { useRef, useState } from "react";
import { motion, useInView, useReducedMotion, useScroll, useSpring, useTransform } from "motion/react";
import { STAR_PATH } from "@/components/brand/Logo";

/*
 * A pocket compass whose needle is tipped with the Migrada star: scammers
 * point students the wrong way, we point them the right one. There is no
 * "N" on the dial: once the needle settles, the star is north.
 *
 * Built in layers so it reads as an object, not a drawing: a stack of discs
 * in 3D gives the rim real thickness, the face is one SVG (rim with a painted
 * grain like Wise's lock, cream dial, ticks, rose, letters, glass), and the
 * needle sits a little above the dial so it shifts against it as the compass
 * turns with the scroll.
 *
 * Motion: on first view the needle swings, overshoots and settles on north,
 * then keeps a faint living tremble. Reduced motion: still, pointing north.
 */

const C = 200; // centre of the 400 × 400 face
const RIM_LAYERS = 14;

function polar(r: number, deg: number) {
  const a = ((deg - 90) * Math.PI) / 180;
  return { x: C + r * Math.cos(a), y: C + r * Math.sin(a) };
}

function Ticks() {
  const ticks = [];
  for (let d = 0; d < 360; d += 5) {
    const cardinal = d % 90 === 0;
    const major = d % 30 === 0;
    const outer = polar(163, d);
    const inner = polar(cardinal ? 147 : major ? 148 : 155, d);
    ticks.push(
      <line
        key={d}
        x1={outer.x}
        y1={outer.y}
        x2={inner.x}
        y2={inner.y}
        stroke={cardinal ? "#00332F" : "#0A0F0E"}
        strokeOpacity={cardinal ? 0.9 : major ? 0.6 : 0.28}
        strokeWidth={cardinal ? 4 : major ? 2.6 : 1.6}
        strokeLinecap="round"
      />,
    );
  }
  return <g>{ticks}</g>;
}

/** An eight-point rose, very faint, under the needle. */
function Rose() {
  const point = (deg: number, long: number, short: number, fill: string) => {
    const tip = polar(long, deg);
    const l = polar(short, deg - 45);
    const r = polar(short, deg + 45);
    return <path key={`${deg}-${long}`} d={`M${C} ${C} L${l.x} ${l.y} L${tip.x} ${tip.y} L${r.x} ${r.y} Z`} fill={fill} />;
  };
  return (
    <g opacity="0.55">
      {[45, 135, 225, 315].map((d) => point(d, 74, 16, "#CFEFEA"))}
      {[0, 90, 180, 270].map((d) => point(d, 104, 20, "#B9E6DF"))}
    </g>
  );
}

function Letters() {
  const letter = (t: string, deg: number, size: number, fill: string) => {
    const p = polar(126, deg);
    return (
      <text key={t} x={p.x} y={p.y} textAnchor="middle" dominantBaseline="central" fontFamily="Onest, system-ui, sans-serif" fontWeight={900} fontSize={size} fill={fill}>
        {t}
      </text>
    );
  };
  return (
    <g>
      {letter("E", 90, 22, "#0A0F0E")}
      {letter("S", 180, 22, "#0A0F0E")}
      {letter("W", 270, 22, "#0A0F0E")}
    </g>
  );
}

/** The needle: slim, two-tone, its north tip is the Migrada star. */
function Needle({ settled }: { settled: boolean }) {
  return (
    <g>
      {/* soft shadow on the dial */}
      <g transform="translate(5 7)" opacity="0.18" filter="url(#needle-blur)">
        <path d={`M${C} 74 L${C + 13} ${C} L${C} 304 L${C - 13} ${C} Z`} fill="#0A0F0E" />
      </g>
      {/* south half */}
      <path d={`M${C - 13} ${C} L${C} 304 L${C + 13} ${C} Z`} fill="url(#needle-south)" />
      {/* north half, split light and shade for a bevel */}
      <path d={`M${C - 13} ${C} L${C} 86 L${C} ${C} Z`} fill="#00443E" />
      <path d={`M${C} 86 L${C + 13} ${C} L${C} ${C} Z`} fill="#00332F" />
      {/* the star at the tip */}
      <g transform={`translate(${C} 72) scale(2.8) translate(-53.36 -18.06)`}>
        <path d={STAR_PATH} fill="#00332F" stroke="#00332F" strokeWidth="1.2" strokeLinejoin="round" />
      </g>
      <g transform={`translate(${C} 72) scale(2.25) translate(-53.36 -18.06)`}>
        <motion.path d={STAR_PATH} fill="#2EE6D6" animate={settled ? { opacity: [1, 0.75, 1] } : { opacity: 1 }} transition={settled ? { duration: 3, repeat: Infinity } : undefined} />
      </g>
      {/* pivot cap */}
      <circle cx={C} cy={C} r="17" fill="url(#cap)" stroke="#0A3F45" strokeWidth="1.5" />
      <circle cx={C} cy={C} r="6" fill="#2EE6D6" />
      <circle cx={C - 5} cy={C - 6} r="3" fill="#FFFFFF" opacity="0.7" />
    </g>
  );
}

export function Compass() {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion() ?? false;
  const seen = useInView(ref, { once: true, margin: "-120px" });
  const [settled, setSettled] = useState(reduced);

  // Turns gently in 3D with the scroll, so the rim's depth shows.
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const turnRaw = useTransform(scrollYProgress, [0, 1], reduced ? [-16, -16] : [-30, -4]);
  const turn = useSpring(turnRaw, { stiffness: 80, damping: 22 });

  return (
    <div ref={ref} className="relative w-[min(72vw,380px)] select-none" style={{ perspective: 1100 }} aria-hidden="true">
      {/* shadow on the page */}
      <div className="absolute inset-x-[10%] bottom-[-6%] h-[14%] rounded-[50%] bg-black/25 blur-2xl" />

      <motion.div
        className="relative"
        style={{ transformStyle: "preserve-3d", rotateX: 24, rotateY: turn, rotateZ: -10 }}
        animate={reduced ? undefined : { y: [0, -8, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      >
        {/* The rim's side wall: discs stacked behind the face. */}
        {Array.from({ length: RIM_LAYERS }).map((_, i) => (
          <div
            key={i}
            className="absolute inset-x-0 rounded-full"
            style={{
              // The rim circle sits below the crown: 64 of the 464 view-box units.
              top: `${(64 / 464) * 100}%`,
              height: `${(400 / 464) * 100}%`,
              transform: `translateZ(${-(i + 1) * 2.2}px)`,
              background: `linear-gradient(135deg, hsl(184 70% ${34 - i}%) 0%, hsl(214 62% ${30 - i}%) 100%)`,
            }}
          />
        ))}

        <svg viewBox="0 -64 400 464" className="relative block h-auto w-full overflow-visible">
          <defs>
            <linearGradient id="rim" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#8FF6EC" />
              <stop offset="0.4" stopColor="#2EC9D6" />
              <stop offset="1" stopColor="#2A55C8" />
            </linearGradient>
            <linearGradient id="rim-inner" x1="1" y1="1" x2="0" y2="0">
              <stop offset="0" stopColor="#7DE9E2" />
              <stop offset="1" stopColor="#1F6FA8" />
            </linearGradient>
            <radialGradient id="dial" cx="0.42" cy="0.38" r="0.75">
              <stop offset="0" stopColor="#FFFEFA" />
              <stop offset="0.75" stopColor="#F3F6F1" />
              <stop offset="1" stopColor="#DCE8E4" />
            </radialGradient>
            <linearGradient id="needle-south" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#E6ECEA" />
              <stop offset="1" stopColor="#BCC8C5" />
            </linearGradient>
            <radialGradient id="cap" cx="0.35" cy="0.3" r="0.8">
              <stop offset="0" stopColor="#B9FBF4" />
              <stop offset="0.5" stopColor="#3CC7D0" />
              <stop offset="1" stopColor="#14588A" />
            </radialGradient>
            <linearGradient id="glass" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.55" />
              <stop offset="0.45" stopColor="#FFFFFF" stopOpacity="0" />
            </linearGradient>
            <filter id="needle-blur" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="3" />
            </filter>
            {/* A painted, chalky grain on the metal, like Wise's renders. */}
            <filter id="paint" x="-5%" y="-5%" width="110%" height="110%">
              <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" seed="4" result="grain" />
              <feColorMatrix in="grain" type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.32 0" result="specks" />
              <feComposite in="specks" in2="SourceGraphic" operator="in" result="specks-in" />
              <feTurbulence type="fractalNoise" baseFrequency="0.02" numOctaves="2" seed="9" result="blots" />
              <feColorMatrix in="blots" type="matrix" values="0 0 0 0 0.08  0 0 0 0 0.22  0 0 0 0 0.78  0 0 0 0.85 -0.28" result="blue" />
              <feComposite in="blue" in2="SourceGraphic" operator="in" result="blue-in" />
              <feMerge>
                <feMergeNode in="SourceGraphic" />
                <feMergeNode in="blue-in" />
                <feMergeNode in="specks-in" />
              </feMerge>
            </filter>
          </defs>

          {/* Crown and bow, for a pocket compass. */}
          <g filter="url(#paint)">
            <circle cx={C} cy={-30} r="26" fill="none" stroke="url(#rim)" strokeWidth="11" />
            <rect x={C - 17} y={-12} width="34" height="22" rx="6" fill="url(#rim)" />
          </g>

          {/* Rim */}
          <g filter="url(#paint)">
            <circle cx={C} cy={C} r="198" fill="url(#rim)" />
            <circle cx={C} cy={C} r="178" fill="url(#rim-inner)" />
          </g>
          <circle cx={C} cy={C} r="171" fill="#0B4A55" />

          {/* Dial */}
          <circle cx={C} cy={C} r="167" fill="url(#dial)" />
          <Rose />
          <Ticks />
          <Letters />

          {/* Needle */}
          <motion.g
            style={{ transformBox: "view-box", transformOrigin: `${C}px ${C}px` }}
            initial={{ rotate: reduced ? 0 : -125 }}
            animate={
              reduced
                ? { rotate: 0 }
                : seen && !settled
                  ? { rotate: [-125, 52, -27, 14, -7, 3, 0] }
                  : settled
                    ? { rotate: [0, 1.8, -1.2, 0.6, 0] }
                    : { rotate: -125 }
            }
            transition={
              settled
                ? { duration: 5, repeat: Infinity, ease: "easeInOut" }
                : { duration: 2.8, delay: 0.25, times: [0, 0.24, 0.44, 0.62, 0.78, 0.9, 1], ease: "easeInOut" }
            }
            onAnimationComplete={() => {
              if (seen && !settled) setSettled(true);
            }}
          >
            <Needle settled={settled} />
          </motion.g>

          {/* Glass: a soft highlight and a thin inner shadow. */}
          <circle cx={C} cy={C} r="167" fill="none" stroke="#0A0F0E" strokeOpacity="0.12" strokeWidth="6" />
          <path d={`M${C - 140} ${C - 20} A 150 150 0 0 1 ${C + 60} ${C - 150} A 170 170 0 0 0 ${C - 140} ${C - 20} Z`} fill="url(#glass)" />
          <ellipse cx={C - 70} cy={C - 92} rx="58" ry="22" transform={`rotate(-38 ${C - 70} ${C - 92})`} fill="#FFFFFF" opacity="0.35" />
        </svg>
      </motion.div>
    </div>
  );
}
